/**
 * Business Directory Registration - 2-Step Form
 * 업체 등록 - 2단계 폼
 *
 * Step 1: 기본정보 (업체명, 업종, 주소, 연락처, 카톡ID, 상세설명, 이미지)
 * Step 2: 영업정보 및 상품/서비스 (영업시간, 상품/서비스 등록)
 */

import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useRoute } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { SignInRequired } from "@/components/SignInRequired";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { extractBusinessLookupMatches } from "@/lib/businessLookup";
import { businessCategories } from "@shared/schema";
import { apiRequest, unwrapPayload } from "@/lib/queryClient";
import {
  ArrowLeft,
  Building2,
  Info,
  ImageIcon,
  MapPin,
  Clock,
  Package,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Edit,
  Trash2,
  X,
} from "lucide-react";
import {
  AddressAutocomplete,
  type AddressDetails,
} from "@/components/AddressAutocomplete";
import { MarketImageUploader } from "@/components/MarketImageUploader";
import { BusinessMap } from "@/components/business/BusinessMap";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { inferZoneIdFromLocation } from "@shared/zones";
import { koreanRestaurantMenuTypes } from "@shared/business-food-tags";

const CREATE_POST_DRAFT_KEY = "ozk_local_create_post_draft_v1";

const regions = [
  { id: "ALL", label: "전체 지역" },
  { id: "NSW", label: "NSW (뉴사우스웨일스)" },
  { id: "VIC", label: "VIC (빅토리아)" },
  { id: "QLD", label: "QLD (퀸즐랜드)" },
  { id: "SA", label: "SA (남호주)" },
  { id: "WA", label: "WA (서호주)" },
  { id: "TAS", label: "TAS (태즈메이니아)" },
  { id: "NT", label: "NT (북부 준주)" },
  { id: "ACT", label: "ACT (수도 준주)" },
];

// 이미지 최적화 사이즈 가이드
const IMAGE_GUIDE = {
  logo: {
    size: "512x512px",
    ratio: "1:1 (정사각형)",
    format: "PNG 또는 JPG",
    maxSize: "500KB",
    description:
      "로고는 정사각형 형태를 권장합니다. 투명 배경 PNG가 가장 좋습니다.",
  },
  cover: {
    size: "1920x1080px",
    ratio: "16:9 (가로형)",
    format: "JPG 또는 PNG",
    maxSize: "2MB",
    description:
      "커버 이미지는 16:9 비율을 권장합니다. 업체 외관이나 대표 이미지를 사용하세요.",
  },
  gallery: {
    size: "1200x800px",
    ratio: "3:2 (가로형)",
    format: "JPG",
    maxSize: "1MB",
    description:
      "갤러리 이미지는 3:2 비율을 권장합니다. 업체 내부, 메뉴, 서비스 사진 등을 업로드하세요.",
  },
};

interface BusinessHours {
  [key: string]: { open: string; close: string; closed: boolean };
}

interface Product {
  id?: number; // 편집 모드에서 사용
  name: string;
  nameKo: string;
  description: string;
  descriptionKo?: string;
  price: string;
  priceCents?: number | null; // API에서 받은 가격 (cents)
  imageUrl: string;
  category?: string;
  isAvailable?: boolean;
}

type ExistingBusiness = {
  claimStatus?: "unclaimed" | "claim_pending" | "claimed" | "disputed" | "revoked";
  businessHours?: BusinessHours;
  openHoursJson?: string | BusinessHours;
  businessName?: string;
  businessNameKo?: string;
  category?: string | number;
  categoryId?: string | number;
  address?: string;
  city?: string;
  suburb?: string;
  postcode?: string;
  state?: string;
  region?: string;
  geoLat?: number | null;
  geoLng?: number | null;
  contactPhone?: string;
  contactEmail?: string;
  website?: string;
  kakaoId?: string;
  description?: string;
  descriptionKo?: string;
  logoUrl?: string;
  coverImageUrl?: string;
  ownerPhotoUrl?: string;
  imageUrls?: string[];
  foodTags?: string[];
};

type ApiProduct = Product & {
  name_ko?: string;
  description_ko?: string;
  price_cents?: number | null;
  image_url?: string;
  is_available?: boolean;
};

type BusinessLookupMatch = {
  id: string;
  name: string;
  phone?: string | null;
  address?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  claimStatus?: string | null;
  match: {
    score: number;
    band: "strong" | "review" | "weak";
    autoLinkEligible: boolean;
    reasons: string[];
  };
};

export default function BusinessDirectoryRegister() {
  const { user, isAuthenticated, membershipLevel } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  // Check if in edit mode
  const [editMatch, editParams] = useRoute<{ id: string }>("/business-directory/:id/edit");
  const businessId = editMatch && editParams ? editParams.id : null;
  const isEditMode = !!businessId;
  const [registrationMode, setRegistrationMode] = useState<"lookup" | "register">(
    isEditMode ? "register" : "lookup",
  );
  const [lookupForm, setLookupForm] = useState({
    name: "",
    phone: "",
    postcode: "",
    website: "",
  });
  const [lookupMatches, setLookupMatches] = useState<BusinessLookupMatch[]>([]);
  const [lookupComplete, setLookupComplete] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [claimingBusinessId, setClaimingBusinessId] = useState<string | null>(null);

  // Fetch existing business if in edit mode
  const { data: existingBusiness, isLoading: loadingBusiness } = useQuery<ExistingBusiness>({
    queryKey: [`/api/business-directory/${businessId}`],
    enabled: !!businessId && isEditMode,
    queryFn: async () => {
      if (!businessId || !user) throw new Error("로그인이 필요합니다.");
      const token = await user.getIdToken();
      const response = await fetch(`/api/business-directory/${encodeURIComponent(businessId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("업체 정보를 불러오지 못했습니다.");
      return response.json() as Promise<ExistingBusiness>;
    },
  });

  // Fetch existing products if in edit mode
  const { data: existingProductsData, isLoading: loadingProducts } = useQuery<{ items: ApiProduct[] }>({
    queryKey: [`/api/businesses/${businessId}/products`],
    enabled: !!businessId && isEditMode && existingBusiness?.claimStatus === "claimed",
    queryFn: async () => {
      if (!businessId) throw new Error("Invalid business ID");
      const response = await fetch(`/api/businesses/${businessId}/products`);
      if (!response.ok) throw new Error("Failed to fetch products");
      return unwrapPayload<{ items: ApiProduct[] }>(await response.json());
    },
  });

  // Step 1: 기본정보
  const [formData, setFormData] = useState({
    businessName: "",
    businessNameKo: "",
    category: "",
    address: "",
    city: "",
    postcode: "",
    state: "",
    geoLat: null as number | null,
    geoLng: null as number | null,
    contactPhone: "",
    contactEmail: "",
    website: "",
    kakaoId: "",
    descriptionKo: "", // 한글 기본
    logoUrl: "", // 서버에 업로드된 로고 이미지 URL
    coverImageUrl: "", // 서버에 업로드된 커버 이미지 URL
    ownerPhotoUrl: "", // 서버에 업로드된 사장님/담당직원 사진 URL
    imageUrls: [] as string[], // 갤러리 이미지들
    foodTags: [] as string[],
    logoStyle: "circle" as
      | "circle"
      | "square"
      | "underline"
      | "gradient"
      | "badge",
    logoColor: "auto" as
      | "auto"
      | "indigo"
      | "purple"
      | "pink"
      | "amber"
      | "emerald"
      | "cyan"
      | "blue"
      | "red"
      | "teal"
      | "orange",
  });

  // 자동 로고 미리보기 상태
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Step 2: 영업정보
  const [businessHours, setBusinessHours] = useState<BusinessHours>({
    monday: { open: "09:00", close: "18:00", closed: false },
    tuesday: { open: "09:00", close: "18:00", closed: false },
    wednesday: { open: "09:00", close: "18:00", closed: false },
    thursday: { open: "09:00", close: "18:00", closed: false },
    friday: { open: "09:00", close: "18:00", closed: false },
    saturday: { open: "10:00", close: "16:00", closed: false },
    sunday: { open: "", close: "", closed: true },
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(null);
  const [deletingProductIndex, setDeletingProductIndex] = useState<number | null>(null);
  const [productImages, setProductImages] = useState<string[]>([]);
  const [newProduct, setNewProduct] = useState<Product>({
    name: "",
    nameKo: "",
    description: "",
    price: "",
    imageUrl: "",
  });
  const [newYouTubeUrl, setNewYouTubeUrl] = useState("");

  // YouTube URL 추가 함수
  const addYouTubeUrl = () => {
    if (!newYouTubeUrl.trim()) {
      toast({
        title: "URL 입력 필요",
        description: "YouTube URL을 입력해주세요.",
        variant: "destructive",
      });
      return;
    }

    // YouTube URL 형식 검증
    if (!/youtube\.com|youtu\.be/.test(newYouTubeUrl)) {
      toast({
        title: "유효하지 않은 URL",
        description: "YouTube URL 형식이 올바르지 않습니다.",
        variant: "destructive",
      });
      return;
    }

    // 중복 확인
    if (formData.imageUrls.includes(newYouTubeUrl)) {
      toast({
        title: "이미 추가된 URL",
        description: "이미 갤러리에 추가된 YouTube 동영상입니다.",
        variant: "destructive",
      });
      return;
    }

    // 최대 개수 확인
    if (formData.imageUrls.length >= 10) {
      toast({
        title: "최대 개수 초과",
        description: "갤러리는 최대 10개까지 추가할 수 있습니다.",
        variant: "destructive",
      });
      return;
    }

    handleChange("imageUrls", [...formData.imageUrls, newYouTubeUrl.trim()]);
    setNewYouTubeUrl("");
    toast({
      title: "추가 완료",
      description: "YouTube 동영상이 갤러리에 추가되었습니다.",
    });
  };

  // Populate form with existing business data when in edit mode
  useEffect(() => {
    if (existingBusiness && isEditMode) {
      // Parse business hours
      if (existingBusiness.businessHours || existingBusiness.openHoursJson) {
        try {
          const hours = typeof existingBusiness.openHoursJson === "string"
            ? JSON.parse(existingBusiness.openHoursJson)
            : existingBusiness.businessHours || existingBusiness.openHoursJson;
          if (hours) {
            setBusinessHours(hours);
          }
        } catch (e) {
          console.warn("Failed to parse business hours:", e);
        }
      }

      // Populate form data
      setFormData({
        businessName: existingBusiness.businessName || "",
        businessNameKo: existingBusiness.businessNameKo || "",
        category: existingBusiness.category?.toString() || existingBusiness.categoryId?.toString() || "",
        address: existingBusiness.address || "",
        city: existingBusiness.city || existingBusiness.suburb || "",
        postcode: existingBusiness.postcode || "",
        state: existingBusiness.state || existingBusiness.region || "",
        geoLat: existingBusiness.geoLat || null,
        geoLng: existingBusiness.geoLng || null,
        contactPhone: existingBusiness.contactPhone || "",
        contactEmail: existingBusiness.contactEmail || "",
        website: existingBusiness.website || "",
        kakaoId: existingBusiness.kakaoId || "",
        descriptionKo: existingBusiness.description || existingBusiness.descriptionKo || "",
        logoUrl: existingBusiness.logoUrl || "",
        coverImageUrl: existingBusiness.coverImageUrl || "",
        ownerPhotoUrl: existingBusiness.ownerPhotoUrl || "",
        imageUrls: existingBusiness.imageUrls || [],
        foodTags:
          existingBusiness.category === "restaurant" &&
          (!existingBusiness.foodTags || existingBusiness.foodTags.length === 0)
            ? ["korean"]
            : existingBusiness.foodTags || [],
        logoStyle: "circle",
        logoColor: "auto",
      });
    }
  }, [existingBusiness, isEditMode]);

  useEffect(() => {
    if (isEditMode) return;

    try {
      const rawDraft = sessionStorage.getItem(CREATE_POST_DRAFT_KEY);
      if (!rawDraft) return;

      const draft = JSON.parse(rawDraft);
      if (draft?.type !== "business") return;

      const draftState = typeof draft.state === "string" ? draft.state : "";
      const draftSuburb = typeof draft.suburb === "string" ? draft.suburb : "";
      setFormData((previous) => ({
        ...previous,
        businessName: typeof draft.title === "string" ? draft.title : "",
        businessNameKo: typeof draft.title === "string" ? draft.title : "",
        descriptionKo:
          typeof draft.description === "string" ? draft.description : "",
        city: draftSuburb,
        state: draftState,
      }));
      sessionStorage.removeItem(CREATE_POST_DRAFT_KEY);
    } catch (error) {
      console.warn(
        "[BusinessDirectoryRegister] Failed to load create draft",
        error,
      );
    }
  }, [isEditMode]);

  // Populate products when in edit mode
  useEffect(() => {
    if (existingProductsData?.items && isEditMode) {
      const formattedProducts: Product[] = existingProductsData.items.map((p) => ({
        id: p.id,
        name: p.name || "",
        nameKo: p.name_ko || "",
        description: p.description || "",
        descriptionKo: p.description_ko || "",
        price: p.price_cents ? (p.price_cents / 100).toFixed(2) : "",
        priceCents: p.price_cents,
        imageUrl: p.image_url || "",
        category: p.category || "",
        isAvailable: p.is_available !== false,
      }));
      setProducts(formattedProducts);
    }
  }, [existingProductsData, isEditMode]);

  const handleChange = (
    field: string,
    value: string | number | null | string[]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // 로고 스타일이나 색상이 변경되면 미리보기 업데이트
    if (
      (field === "logoStyle" || field === "logoColor") &&
      formData.businessName
    ) {
      updateLogoPreview(
        formData.businessName,
        field === "logoStyle" ? (value as string) : formData.logoStyle,
        field === "logoColor" ? (value as string) : formData.logoColor
      );
    }
  };

  // 자동 로고 미리보기 업데이트
  const updateLogoPreview = async (
    name: string,
    style: string,
    color: string
  ) => {
    if (!name || name.trim().length === 0) {
      setLogoPreview(null);
      setCoverPreview(null);
      return;
    }

    setLoadingPreview(true);
    try {
      const logoRes = await fetch(
        `/api/businesses/generate-logo-preview?name=${encodeURIComponent(
          name
        )}&style=${style}&color=${color}&type=logo`
      );
      const logoData = await logoRes.json();
      setLogoPreview(logoData.imageUrl);

      const coverRes = await fetch(
        `/api/businesses/generate-logo-preview?name=${encodeURIComponent(
          name
        )}&style=${style}&color=${color}&type=cover`
      );
      const coverData = await coverRes.json();
      setCoverPreview(coverData.imageUrl);
    } catch (error) {
      console.error("Failed to load logo preview:", error);
    } finally {
      setLoadingPreview(false);
    }
  };

  // 업체명 변경 시 미리보기 업데이트
  const handleBusinessNameChange = (value: string) => {
    handleChange("businessName", value);
    if (value && value.trim().length > 0) {
      updateLogoPreview(value, formData.logoStyle, formData.logoColor);
    } else {
      setLogoPreview(null);
      setCoverPreview(null);
    }
  };

  const handleAddressChange = (address: string, details?: AddressDetails) => {
    // fullAddress가 있으면 그것을 사용, 없으면 address 사용
    const fullAddress = details?.fullAddress || address;
    handleChange("address", fullAddress);

    if (details) {
      // 모든 주소 필드 필수로 채우기
      handleChange("city", details.city || "");
      handleChange("postcode", details.postcode || "");
      handleChange("state", details.state || "");

      // Google Maps에서 좌표 가져오기 (필수)
      if (details.lat && details.lng) {
        handleChange("geoLat", details.lat);
        handleChange("geoLng", details.lng);
      } else {
        // 좌표가 없으면 경고
        console.warn(
          "⚠️ 주소 좌표 정보가 없습니다. 지도 표시가 불가능할 수 있습니다."
        );
      }
    }
  };

  const handleBusinessHoursChange = (
    day: string,
    field: string,
    value: string | boolean
  ) => {
    setBusinessHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const addProduct = () => {
    if (!newProduct.name || !newProduct.nameKo) {
      toast({
        title: "상품명 입력 필요",
        description: "상품명(영문/한글)을 모두 입력해주세요.",
        variant: "destructive",
      });
      return;
    }

    // 이미지 URL 설정
    const productWithImage = {
      ...newProduct,
      imageUrl: productImages.length > 0 ? productImages[0] : newProduct.imageUrl,
    };

    if (editingProductIndex !== null) {
      // 수정 모드
      const updatedProducts = [...products];
      updatedProducts[editingProductIndex] = productWithImage;
      setProducts(updatedProducts);
      setEditingProductIndex(null);
    } else {
      // 추가 모드
      setProducts([...products, productWithImage]);
    }

    // 폼 초기화
    setNewProduct({
      name: "",
      nameKo: "",
      description: "",
      price: "",
      imageUrl: "",
    });
    setProductImages([]);
    setShowProductForm(false);
  };

  const editProduct = (index: number) => {
    const product = products[index];
    setNewProduct({
      ...product,
      price: product.priceCents ? (product.priceCents / 100).toFixed(2) : product.price,
    });
    setProductImages(product.imageUrl ? [product.imageUrl] : []);
    setEditingProductIndex(index);
    setShowProductForm(true);
  };

  const removeProduct = (index: number) => {
    setDeletingProductIndex(index);
  };

  const confirmRemoveProduct = () => {
    if (deletingProductIndex !== null) {
      setProducts(products.filter((_, i) => i !== deletingProductIndex));
      setDeletingProductIndex(null);
    }
  };

  const cancelProductForm = () => {
    setNewProduct({
      name: "",
      nameKo: "",
      description: "",
      price: "",
      imageUrl: "",
    });
    setProductImages([]);
    setEditingProductIndex(null);
    setShowProductForm(false);
  };

  const validateStep1 = (): boolean => {
    if (!formData.businessName) {
      toast({
        title: "업체명 입력 필요",
        description: "업체명(영문)을 입력해주세요.",
        variant: "destructive",
      });
      return false;
    }
    if (!formData.category) {
      toast({
        title: "업종 선택 필요",
        description: "업종을 선택해주세요.",
        variant: "destructive",
      });
      return false;
    }
    // 주소 검증 강화: 주소, 도시, 주, 우편번호, 좌표 모두 확인
    if (!formData.address || formData.address.trim().length < 5) {
      toast({
        title: "주소 입력 필요",
        description: "정확한 주소를 검색하여 입력해주세요.",
        variant: "destructive",
      });
      return false;
    }
    if (!formData.city || formData.city.trim().length === 0) {
      toast({
        title: "도시 정보 필요",
        description:
          "주소 검색 결과에서 도시 정보가 자동으로 채워지지 않았습니다. 주소를 다시 검색해주세요.",
        variant: "destructive",
      });
      return false;
    }
    if (!formData.state || formData.state.trim().length === 0) {
      toast({
        title: "주/구역 정보 필요",
        description:
          "주소 검색 결과에서 주/구역 정보가 자동으로 채워지지 않았습니다. 주소를 다시 검색해주세요.",
        variant: "destructive",
      });
      return false;
    }
    if (!formData.postcode || formData.postcode.trim().length === 0) {
      toast({
        title: "우편번호 필요",
        description:
          "주소 검색 결과에서 우편번호가 자동으로 채워지지 않았습니다. 주소를 다시 검색해주세요.",
        variant: "destructive",
      });
      return false;
    }
    if (!formData.geoLat || !formData.geoLng) {
      toast({
        title: "위치 정보 필요",
        description:
          "주소 검색 결과에서 위치 좌표가 자동으로 채워지지 않았습니다. 주소를 다시 검색해주세요.",
        variant: "destructive",
      });
      return false;
    }
    if (!formData.contactPhone) {
      toast({
        title: "전화번호 입력 필요",
        description: "연락 가능한 전화번호를 입력해주세요.",
        variant: "destructive",
      });
      return false;
    }
    if (!formData.kakaoId) {
      toast({
        title: "카카오톡 ID 입력 필요",
        description: "카카오톡 ID는 필수입니다.",
        variant: "destructive",
      });
      return false;
    }
    if (!formData.descriptionKo) {
      toast({
        title: "상세 설명 입력 필요",
        description: "한글 상세 설명을 입력해주세요.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else {
      setLocation("/business-directory");
    }
  };

  const handleBusinessLookup = async () => {
    if (lookupForm.name.trim().length < 2) {
      toast({
        title: "업체명을 입력해주세요",
        description: "중복 업체 확인을 위해 업체명은 2자 이상 필요합니다.",
        variant: "destructive",
      });
      return;
    }
    setLookupLoading(true);
    try {
      const response = await apiRequest("POST", "/api/business-directory/matches", lookupForm);
      const data = await response.json();
      setLookupMatches(extractBusinessLookupMatches<BusinessLookupMatch>(data));
      setLookupComplete(true);
    } catch (error: any) {
      toast({
        title: "업체 검색 실패",
        description: error.message || "기존 업체를 검색하지 못했습니다.",
        variant: "destructive",
      });
    } finally {
      setLookupLoading(false);
    }
  };

  const handleClaimRequest = async (match: BusinessLookupMatch) => {
    setClaimingBusinessId(match.id);
    try {
      await apiRequest("POST", `/api/businesses/${match.id}/claims`, {
        verificationMethod: "manual_review",
        note: "Claim-first registration lookup에서 신청",
      });
      toast({
        title: "소유권 요청 접수",
        description: "승인 전까지 기존 업체 정보는 변경되지 않습니다.",
      });
      setLocation(`/business-directory/${match.id}/claim`);
    } catch (error: any) {
      toast({
        title: "소유권 요청 실패",
        description: error.message || "소유권 요청을 접수하지 못했습니다.",
        variant: "destructive",
      });
    } finally {
      setClaimingBusinessId(null);
    }
  };

  const continueWithNewBusiness = () => {
    setFormData((previous) => ({
      ...previous,
      businessName: lookupForm.name,
      contactPhone: lookupForm.phone,
      postcode: lookupForm.postcode,
      website: lookupForm.website,
    }));
    setRegistrationMode("register");
  };

  const handleSubmit = async () => {
    if (!isAuthenticated || !user) {
      toast({
        title: "로그인 필요",
        description: "업체를 등록하려면 로그인이 필요합니다.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // 영업시간 JSON 변환
      const openHoursJson = JSON.stringify(businessHours);
      const zoneId = inferZoneIdFromLocation({
        suburb: formData.city,
        city: formData.city,
        state: formData.state,
      });

      if (isEditMode && businessId) {
        // 업체 수정
        const payload = {
          ...formData,
          openHoursJson,
          logoStyle: formData.logoStyle,
          logoColor: formData.logoColor,
          ownerPhotoUrl: formData.ownerPhotoUrl || null,
          zone_id: zoneId,
          zoneId,
        };

        const token = await user.getIdToken();
        const businessResponse = await apiRequest(
          "PUT",
          `/api/business-directory/${businessId}`,
          payload,
          {
            Authorization: `Bearer ${token}`,
          }
        );

        // 상품 업데이트 처리 (편집 모드)
        if (products.length > 0) {
          // 기존 상품 목록 가져오기
          const existingProductsResponse = await fetch(`/api/businesses/${businessId}/products`);
          const existingProductsData = existingProductsResponse.ok
            ? unwrapPayload<{ items: ApiProduct[] }>(await existingProductsResponse.json())
            : { items: [] };
          const existingProducts = existingProductsData.items || [];

          // 기존 상품 ID 목록
          const existingProductIds = new Set(
            existingProducts.map((p: any) => p.id).filter((id: any) => id)
          );

          // 현재 상품 ID 목록
          const currentProductIds = new Set(
            products.map((p) => p.id).filter((id) => id !== undefined)
          );

          // 삭제할 상품 (기존에 있지만 현재 목록에 없는 것)
          const productsToDelete = existingProducts.filter(
            (p: any) => !currentProductIds.has(p.id)
          );

          // 삭제 처리
          for (const productToDelete of productsToDelete) {
            try {
              await apiRequest(
                "DELETE",
                `/api/businesses/${businessId}/products/${productToDelete.id}`,
                {},
                {
                  Authorization: `Bearer ${token}`,
                }
              );
            } catch (error) {
              console.error("Failed to delete product:", error);
            }
          }

          // 추가/수정할 상품 처리
          for (const product of products) {
            try {
              const productPayload = {
                name: product.name,
                name_ko: product.nameKo,
                description: product.description || null,
                description_ko: product.descriptionKo || null,
                price_cents: product.price
                  ? Math.round(parseFloat(product.price) * 100)
                  : null,
                currency: "AUD",
                image_url: product.imageUrl || null,
                category: product.category || null,
                is_available: product.isAvailable !== false,
              };

              if (product.id && existingProductIds.has(product.id)) {
                // 수정
                await apiRequest(
                  "PUT",
                  `/api/businesses/${businessId}/products/${product.id}`,
                  productPayload,
                  {
                    Authorization: `Bearer ${token}`,
                  }
                );
              } else {
                // 추가
                await apiRequest(
                  "POST",
                  `/api/businesses/${businessId}/products`,
                  productPayload,
                  {
                    Authorization: `Bearer ${token}`,
                  }
                );
              }
            } catch (error) {
              console.error("Failed to update product:", error);
            }
          }
        }

        toast({
          title: "수정 완료",
          description: "업체 정보가 수정되었습니다.",
        });

        setLocation(`/business-directory/${businessId}`);
        return;
      }

      // 업체 등록
      const payload = {
        ...formData,
        userId: user.uid,
        ownerName: user.displayName || "익명",
        ownerEmail: user.email || "",
        membershipTier:
          membershipLevel === "platinum"
            ? "platinum"
            : membershipLevel === "premium"
            ? "premium"
            : "free",
        status: "pending",
        openHoursJson,
        logoStyle: formData.logoStyle,
        logoColor: formData.logoColor,
        ownerPhotoUrl: formData.ownerPhotoUrl || null, // 사장님/담당직원 사진
        zone_id: zoneId,
        zoneId,
        averageRating: "0",
        reviewCount: 0,
        views: 0,
        clicks: 0,
        bookmarks: 0,
        isVerified: false,
      };

      const businessResponse = await apiRequest(
        "POST",
        "/api/business-directory",
        payload
      );
      const createdBusiness = await businessResponse.json() as { id?: string };

      toast({
        title: "업체 등록 접수",
        description:
          "초안이 저장되었습니다. 중복 및 소유권 확인 후 공개되며, 승인 전에는 상품·QR·유료 기능이 활성화되지 않습니다.",
      });

      setLocation(createdBusiness.id ? `/business-directory/${createdBusiness.id}/claim` : "/business-directory");
    } catch (error: any) {
      toast({
        title: "등록 실패",
        description: error.message || "업체 등록 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <SignInRequired
        title={isEditMode ? "업체 정보 수정" : "업체 등록"}
        description={isEditMode
          ? "업체 소유자 확인을 위해 로그인해주세요. 로그인 후 수정 화면을 계속 이용할 수 있습니다."
          : "업체 소유권 확인과 등록 진행 알림을 위해 로그인해주세요. 로그인 후 업체 정보를 바로 입력할 수 있습니다."}
        backHref="/business-directory"
        backLabel="업체찾기 목록"
      />
    );
  }

  if (isEditMode && loadingBusiness) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">업체 정보를 불러오는 중...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isEditMode && registrationMode === "lookup") {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto max-w-3xl px-4 py-8">
          <Button variant="outline" onClick={() => setLocation("/business-directory")} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />목록으로
          </Button>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Building2 className="h-6 w-6 text-primary" />내 업체 찾기 또는 등록
              </CardTitle>
              <CardDescription>
                OZK에 이미 등록된 업체인지 먼저 확인합니다. 기존 업체가 있으면 새로 만들지 않고 소유권을 요청합니다.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="lookup-name">업체명 *</Label>
                  <Input id="lookup-name" value={lookupForm.name} onChange={(event) => setLookupForm((value) => ({ ...value, name: event.target.value }))} placeholder="한글 또는 영문 업체명" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lookup-phone">전화번호</Label>
                  <Input id="lookup-phone" value={lookupForm.phone} onChange={(event) => setLookupForm((value) => ({ ...value, phone: event.target.value }))} placeholder="02 9000 0000" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lookup-postcode">우편번호</Label>
                  <Input id="lookup-postcode" value={lookupForm.postcode} onChange={(event) => setLookupForm((value) => ({ ...value, postcode: event.target.value }))} placeholder="2000" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="lookup-website">웹사이트</Label>
                  <Input id="lookup-website" value={lookupForm.website} onChange={(event) => setLookupForm((value) => ({ ...value, website: event.target.value }))} placeholder="https://example.com.au" />
                </div>
              </div>
              <Button onClick={handleBusinessLookup} disabled={lookupLoading} className="w-full">
                {lookupLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                내 업체 찾기
              </Button>

              {lookupComplete && lookupMatches.length > 0 && (
                <div className="space-y-3 border-t pt-5">
                  <h2 className="font-semibold">다음 업체 중 귀하의 업체가 있나요?</h2>
                  {lookupMatches.map((match) => (
                    <Card key={match.id} className={match.match.band === "strong" ? "border-primary" : ""}>
                      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="font-semibold">{match.name}</p>
                          <p className="text-sm text-muted-foreground">{[match.address, match.suburb, match.state, match.postcode].filter(Boolean).join(" · ")}</p>
                          {match.phone ? <p className="text-sm text-muted-foreground">{match.phone}</p> : null}
                        </div>
                        <Button variant="outline" onClick={() => handleClaimRequest(match)} disabled={claimingBusinessId === match.id}>
                          {claimingBusinessId === match.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                          이 업체 소유권 요청
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                  <Button variant="ghost" onClick={continueWithNewBusiness} className="w-full">
                    내 업체가 목록에 없습니다
                  </Button>
                </div>
              )}

              {lookupComplete && lookupMatches.length === 0 && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    일치하는 업체가 없습니다. 확인 절차를 거칠 신규 업체 등록을 시작할 수 있습니다.
                    <Button onClick={continueWithNewBusiness}>새 업체 등록</Button>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <Button variant="outline" onClick={handleBack} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {step === 1 ? "목록으로" : "이전 단계"}
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">{isEditMode ? "업체 수정" : "업체 등록"}</h1>
          </div>
          <p className="text-muted-foreground">
            {step === 1
              ? isEditMode ? "기본 정보를 수정해주세요" : "기본 정보를 입력해주세요"
              : "영업 정보 및 상품/서비스를 등록해주세요"}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-6 flex items-center gap-4">
          <div
            className={`flex items-center gap-2 ${
              step >= 1 ? "text-primary" : "text-muted-foreground"
            }`}
          >
            {step > 1 ? (
              <CheckCircle2 className="h-5 w-5" />
            ) : (
              <div
                className={`h-5 w-5 rounded-full border-2 ${
                  step === 1 ? "border-primary bg-primary" : "border-muted"
                }`}
              />
            )}
            <span className="font-medium">기본 정보</span>
          </div>
          <div className="flex-1 h-0.5 bg-muted">
            <div
              className={`h-full ${step >= 2 ? "bg-primary" : "bg-muted"}`}
              style={{ width: step >= 2 ? "100%" : "0%" }}
            />
          </div>
          <div
            className={`flex items-center gap-2 ${
              step >= 2 ? "text-primary" : "text-muted-foreground"
            }`}
          >
            {step > 2 ? (
              <CheckCircle2 className="h-5 w-5" />
            ) : (
              <div
                className={`h-5 w-5 rounded-full border-2 ${
                  step === 2 ? "border-primary bg-primary" : "border-muted"
                }`}
              />
            )}
            <span className="font-medium">영업 정보</span>
          </div>
        </div>

        {/* Step 1: 기본 정보 */}
        {step === 1 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleNext();
            }}
            className="space-y-6"
          >
            {/* 기본 정보 */}
            <Card>
              <CardHeader>
                <CardTitle>기본 정보</CardTitle>
                <CardDescription>
                  업체의 기본 정보를 입력해주세요
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="businessName">
                      업체명 (영문) <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="businessName"
                      value={formData.businessName}
                      onChange={(e) =>
                        handleBusinessNameChange(e.target.value)
                      }
                      placeholder="ABC Korean Restaurant"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="businessNameKo">업체명 (한글)</Label>
                    <Input
                      id="businessNameKo"
                      value={formData.businessNameKo}
                      onChange={(e) =>
                        handleChange("businessNameKo", e.target.value)
                      }
                      placeholder="ABC 한식당"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">
                    업종 <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => {
                      handleChange("category", value);
                      if (value === "restaurant" && formData.foodTags.length === 0) {
                        handleChange("foodTags", ["korean"]);
                      } else if (value !== "restaurant") {
                        handleChange("foodTags", []);
                      }
                    }}
                  >
                    <SelectTrigger id="category">
                      <SelectValue placeholder="업종 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      {businessCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.category === "restaurant" && (
                  <div className="space-y-3 rounded-lg border p-4">
                    <div>
                      <Label>음식·메뉴 유형</Label>
                      <p className="mt-1 text-xs text-muted-foreground">
                        해당하는 유형을 모두 선택하세요. 새 식당은 한식이 기본으로 선택됩니다.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {koreanRestaurantMenuTypes.map((type) => {
                        const selected = formData.foodTags.includes(type.id);
                        return (
                          <Button
                            key={type.id}
                            type="button"
                            size="sm"
                            variant={selected ? "default" : "outline"}
                            aria-pressed={selected}
                            onClick={() =>
                              handleChange(
                                "foodTags",
                                selected
                                  ? formData.foodTags.filter((tag) => tag !== type.id)
                                  : [...formData.foodTags, type.id],
                              )
                            }
                          >
                            {type.label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 주소 검색 및 지도 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>
                      구체적인 주소 검색{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <AddressAutocomplete
                      value={formData.address}
                      onChange={handleAddressChange}
                      onDetailsChange={(details) => {
                        // 주소 상세 정보가 변경될 때마다 모든 필드 업데이트
                        handleAddressChange(details.fullAddress, details);
                      }}
                      placeholder="구체적인 주소를 검색하세요 (예: 160 Pitt St, Sydney NSW 2000)"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      정확한 주소를 검색하면 자동으로 도시, 주, 우편번호, 위치
                      좌표가 채워집니다
                    </p>

                    {/* 주소 정보 미리보기 */}
                    {formData.address && (
                      <div className="mt-2 p-3 bg-muted rounded-md space-y-1 text-xs">
                        <div className="flex items-center gap-2 font-medium">
                          <MapPin className="h-3 w-3" />
                          <span>주소: {formData.address}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {formData.city && (
                            <div className="flex items-center gap-1">
                              <span className="text-muted-foreground">
                                도시:
                              </span>
                              <span className="font-medium">
                                {formData.city}
                              </span>
                            </div>
                          )}
                          {formData.state && (
                            <div className="flex items-center gap-1">
                              <span className="text-muted-foreground">
                                주/구역:
                              </span>
                              <span className="font-medium">
                                {formData.state}
                              </span>
                            </div>
                          )}
                          {formData.postcode && (
                            <div className="flex items-center gap-1">
                              <span className="text-muted-foreground">
                                우편번호:
                              </span>
                              <span className="font-medium">
                                {formData.postcode}
                              </span>
                            </div>
                          )}
                          {formData.geoLat && formData.geoLng ? (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle2 className="h-3 w-3" />
                              <span>위치 좌표 확인됨</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-amber-600">
                              <AlertCircle className="h-3 w-3" />
                              <span>위치 좌표 필요</span>
                            </div>
                          )}
                        </div>
                        {(!formData.city ||
                          !formData.state ||
                          !formData.postcode ||
                          !formData.geoLat ||
                          !formData.geoLng) && (
                          <Alert className="mt-2">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription className="text-xs">
                              주소 정보가 완전하지 않습니다. 주소를 다시
                              검색하여 모든 정보가 자동으로 채워지도록 해주세요.
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    )}
                  </div>

                  {formData.geoLat && formData.geoLng && (
                    <div className="space-y-2">
                      <Label>위치 확인</Label>
                      <BusinessMap
                        lat={formData.geoLat}
                        lng={formData.geoLng}
                        address={formData.address}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">도시</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => handleChange("city", e.target.value)}
                        placeholder="Sydney"
                        readOnly
                      />
                      <p className="text-xs text-muted-foreground">
                        주소 선택 시 자동 입력
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="postcode">우편번호</Label>
                      <Input
                        id="postcode"
                        value={formData.postcode}
                        onChange={(e) =>
                          handleChange("postcode", e.target.value)
                        }
                        placeholder="2000"
                        maxLength={4}
                        readOnly
                      />
                      <p className="text-xs text-muted-foreground">
                        주소 선택 시 자동 입력
                      </p>
                    </div>
                  </div>
                </div>

                {/* 연락처 */}
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-semibold">연락처 정보</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contactPhone">
                        전화번호 <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="contactPhone"
                        type="tel"
                        value={formData.contactPhone}
                        onChange={(e) =>
                          handleChange("contactPhone", e.target.value)
                        }
                        placeholder="02-1234-5678 또는 0412-345-678"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="kakaoId">
                        카카오톡 ID <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="kakaoId"
                        value={formData.kakaoId}
                        onChange={(e) =>
                          handleChange("kakaoId", e.target.value)
                        }
                        placeholder="kakaotalk_id"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contactEmail">이메일</Label>
                      <Input
                        id="contactEmail"
                        type="email"
                        value={formData.contactEmail}
                        onChange={(e) =>
                          handleChange("contactEmail", e.target.value)
                        }
                        placeholder="contact@example.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website">웹사이트</Label>
                      <Input
                        id="website"
                        type="url"
                        value={formData.website}
                        onChange={(e) =>
                          handleChange("website", e.target.value)
                        }
                        placeholder="https://www.example.com"
                      />
                    </div>
                  </div>

                  {/* 사장님/담당직원 사진 */}
                  <div className="space-y-4 pt-4 border-t">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label>사장님 또는 담당직원 사진</Label>
                        <span className="text-xs text-muted-foreground">
                          (선택사항)
                        </span>
                      </div>
                      <Alert>
                        <Info className="h-4 w-4" />
                        <AlertDescription>
                          <div className="space-y-1">
                            <p className="font-medium text-amber-600">
                              ⚠️ 꼭 실제 인물 사진으로 넣으세요
                            </p>
                            <p className="text-sm">
                              실제 사장님 또는 담당직원의 사진을 등록하면 고객
                              신뢰도가 크게 향상됩니다.
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                              • 실제 인물 사진 권장 (얼굴이 보이는 사진) • 업체
                              신뢰도 향상에 도움이 됩니다 • 최적 사이즈:
                              400x400px (정사각형)
                            </p>
                          </div>
                        </AlertDescription>
                      </Alert>

                      <MarketImageUploader
                        maxImages={1}
                        onImagesChange={(paths) => {
                          handleChange(
                            "ownerPhotoUrl",
                            paths.length > 0 ? paths[0] : ""
                          );
                        }}
                        userId={user?.uid}
                        initialImages={
                          formData.ownerPhotoUrl ? [formData.ownerPhotoUrl] : []
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* 상세 설명 (한글 기본) */}
                <div className="space-y-2 pt-4 border-t">
                  <Label htmlFor="descriptionKo">
                    상세 설명 (한글) <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="descriptionKo"
                    value={formData.descriptionKo}
                    onChange={(e) =>
                      handleChange("descriptionKo", e.target.value)
                    }
                    placeholder="업체에 대한 상세한 설명을 한글로 입력해주세요"
                    rows={6}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    업체의 특징, 서비스, 메뉴 등을 자세히 소개해주세요
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* 이미지 */}
            <Card>
              <CardHeader>
                <CardTitle>업체 이미지</CardTitle>
                <CardDescription>
                  모든 이미지는 서버에 직접 업로드됩니다. 이미지를 업로드하지
                  않으면 업체명으로 자동 로고가 생성됩니다.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* 이미지 사이즈 가이드 */}
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-2 mt-2">
                      <div>
                        <strong>로고:</strong> {IMAGE_GUIDE.logo.size} (
                        {IMAGE_GUIDE.logo.ratio}) - {IMAGE_GUIDE.logo.maxSize}
                        <br />
                        <span className="text-xs text-muted-foreground">
                          {IMAGE_GUIDE.logo.description}
                        </span>
                      </div>
                      <div>
                        <strong>커버:</strong> {IMAGE_GUIDE.cover.size} (
                        {IMAGE_GUIDE.cover.ratio}) - {IMAGE_GUIDE.cover.maxSize}
                        <br />
                        <span className="text-xs text-muted-foreground">
                          {IMAGE_GUIDE.cover.description}
                        </span>
                      </div>
                      <div>
                        <strong>갤러리:</strong> {IMAGE_GUIDE.gallery.size} (
                        {IMAGE_GUIDE.gallery.ratio}) -{" "}
                        {IMAGE_GUIDE.gallery.maxSize}
                        <br />
                        <span className="text-xs text-muted-foreground">
                          {IMAGE_GUIDE.gallery.description}
                        </span>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>

                {/* 로고 이미지 업로드 */}
                <div className="space-y-4 p-4 border rounded-md">
                  <div className="flex items-center gap-2 mb-2">
                    <ImageIcon className="w-5 h-5" />
                    <h4 className="font-semibold">로고 이미지</h4>
                  </div>

                  <MarketImageUploader
                    maxImages={1}
                    onImagesChange={(paths) => {
                      handleChange("logoUrl", paths.length > 0 ? paths[0] : "");
                    }}
                    userId={user?.uid}
                    initialImages={formData.logoUrl ? [formData.logoUrl] : []}
                  />

                  <p className="text-xs text-muted-foreground">
                    로고를 업로드하지 않으면 아래에서 스타일과 색상을 선택하여
                    자동 로고를 생성할 수 있습니다.
                  </p>
                </div>

                {/* 커버 이미지 업로드 */}
                <div className="space-y-4 p-4 border rounded-md">
                  <div className="flex items-center gap-2 mb-2">
                    <ImageIcon className="w-5 h-5" />
                    <h4 className="font-semibold">커버 이미지</h4>
                  </div>

                  <MarketImageUploader
                    maxImages={1}
                    onImagesChange={(paths) => {
                      handleChange(
                        "coverImageUrl",
                        paths.length > 0 ? paths[0] : ""
                      );
                    }}
                    userId={user?.uid}
                    initialImages={
                      formData.coverImageUrl ? [formData.coverImageUrl] : []
                    }
                  />

                  <p className="text-xs text-muted-foreground">
                    커버 이미지를 업로드하지 않으면 자동 로고와 동일한 색상으로
                    커버 이미지가 생성됩니다.
                  </p>
                </div>

                {/* 갤러리 이미지 및 YouTube 동영상 */}
                <div className="space-y-4 p-4 border rounded-md">
                  <div className="flex items-center gap-2 mb-2">
                    <ImageIcon className="w-5 h-5" />
                    <h4 className="font-semibold">
                      갤러리 (이미지 및 YouTube 동영상)
                      <span className="text-sm font-normal text-muted-foreground ml-2">
                        (
                        최대 10개
                        )
                      </span>
                    </h4>
                  </div>

                  <MarketImageUploader
                    maxImages={10}
                    onImagesChange={(paths) => handleChange("imageUrls", paths)}
                    userId={user?.uid}
                    initialImages={formData.imageUrls.filter((url) => !/youtube\.com|youtu\.be/.test(url))}
                  />

                  <div className="space-y-2 pt-2 border-t">
                    <Label>YouTube 동영상 URL 추가</Label>
                    <div className="flex gap-2">
                      <Input
                        type="url"
                        placeholder="https://www.youtube.com/watch?v=... 또는 https://youtu.be/..."
                        value={newYouTubeUrl}
                        onChange={(e) => setNewYouTubeUrl(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addYouTubeUrl();
                          }
                        }}
                      />
                      <Button type="button" onClick={addYouTubeUrl} variant="outline">
                        추가
                      </Button>
                    </div>
                    {formData.imageUrls.some((url) => /youtube\.com|youtu\.be/.test(url)) && (
                      <div className="mt-2 space-y-1">
                        <p className="text-sm text-muted-foreground">추가된 YouTube 동영상:</p>
                        {formData.imageUrls
                          .filter((url) => /youtube\.com|youtu\.be/.test(url))
                          .map((url, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 bg-muted rounded text-sm"
                            >
                              <span className="truncate flex-1">{url}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newUrls = formData.imageUrls.filter((u) => u !== url);
                                  handleChange("imageUrls", newUrls);
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground">
                    업체 내부, 메뉴, 서비스 사진 또는 YouTube 동영상 URL을 추가하세요.
                  </p>
                </div>

                {/* 자동 로고 생성 옵션 (로고/커버가 없을 때만 표시) */}
                {!formData.logoUrl && !formData.coverImageUrl && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="space-y-2">
                      <Label>자동 로고 생성 옵션</Label>
                      <p className="text-xs text-muted-foreground">
                        업체명을 입력하고 스타일과 색상을 선택하면 미리보기를
                        확인할 수 있습니다.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>로고 스타일 선택</Label>
                      <div className="grid grid-cols-5 gap-2">
                        {[
                          { value: "circle", label: "원형", icon: "⭕" },
                          { value: "square", label: "사각형", icon: "⬜" },
                          { value: "underline", label: "밑줄", icon: "➖" },
                          {
                            value: "gradient",
                            label: "그라데이션",
                            icon: "🌈",
                          },
                          { value: "badge", label: "배지", icon: "🏷️" },
                        ].map((style) => (
                          <button
                            key={style.value}
                            type="button"
                            onClick={() =>
                              handleChange("logoStyle", style.value)
                            }
                            className={`p-3 border-2 rounded-lg text-center transition-all ${
                              formData.logoStyle === style.value
                                ? "border-primary bg-primary/10"
                                : "border-muted hover:border-primary/50"
                            }`}
                          >
                            <div className="text-2xl mb-1">{style.icon}</div>
                            <div className="text-xs">{style.label}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>로고 색상 선택</Label>
                      <div className="grid grid-cols-6 gap-2">
                        {[
                          { value: "auto", label: "자동", color: "#6366f1" },
                          {
                            value: "indigo",
                            label: "인디고",
                            color: "#6366f1",
                          },
                          { value: "purple", label: "퍼플", color: "#8b5cf6" },
                          { value: "pink", label: "핑크", color: "#ec4899" },
                          { value: "amber", label: "앰버", color: "#f59e0b" },
                          {
                            value: "emerald",
                            label: "에메랄드",
                            color: "#10b981",
                          },
                          { value: "cyan", label: "시안", color: "#06b6d4" },
                          { value: "blue", label: "블루", color: "#3b82f6" },
                          { value: "red", label: "레드", color: "#ef4444" },
                          { value: "teal", label: "틸", color: "#14b8a6" },
                          {
                            value: "orange",
                            label: "오렌지",
                            color: "#f97316",
                          },
                        ].map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            onClick={() =>
                              handleChange("logoColor", color.value)
                            }
                            className={`p-2 border-2 rounded-lg text-center transition-all ${
                              formData.logoColor === color.value
                                ? "border-primary ring-2 ring-primary/20"
                                : "border-muted hover:border-primary/50"
                            }`}
                          >
                            <div
                              className="w-full h-8 rounded mb-1"
                              style={{ backgroundColor: color.color }}
                            />
                            <div className="text-xs">{color.label}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* 로고 미리보기 */}
                    {formData.businessName &&
                      formData.businessName.trim().length > 0 && (
                        <div className="space-y-4 pt-4 border-t">
                          <Label>로고 미리보기</Label>
                          {loadingPreview ? (
                            <div className="flex items-center justify-center p-8 border rounded-md">
                              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-4">
                              {logoPreview && (
                                <div className="space-y-2">
                                  <Label className="text-xs">
                                    로고 미리보기
                                  </Label>
                                  <div className="border rounded-md p-4 bg-muted/50 flex items-center justify-center">
                                    <img
                                      src={logoPreview}
                                      alt="로고 미리보기"
                                      className="w-32 h-32 object-contain"
                                    />
                                  </div>
                                </div>
                              )}
                              {coverPreview && (
                                <div className="space-y-2">
                                  <Label className="text-xs">
                                    커버 미리보기
                                  </Label>
                                  <div className="border rounded-md p-2 bg-muted/50">
                                    <img
                                      src={coverPreview}
                                      alt="커버 미리보기"
                                      className="w-full aspect-video object-cover rounded"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground">
                            업체명을 변경하거나 스타일/색상을 선택하면
                            미리보기가 자동으로 업데이트됩니다.
                          </p>
                        </div>
                      )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Next Button */}
            <div className="flex gap-4 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={loading}
              >
                취소
              </Button>
              <Button type="submit" disabled={loading || loadingBusiness}>
                다음 단계
              </Button>
            </div>
          </form>
        )}

        {/* Step 2: 영업 정보 */}
        {step === 2 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            className="space-y-6"
          >
            {/* 영업 시간 */}
            <Card>
              <CardHeader>
                <CardTitle>영업 시간</CardTitle>
                <CardDescription>
                  요일별 영업 시간을 입력해주세요
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: "monday", label: "월요일" },
                  { key: "tuesday", label: "화요일" },
                  { key: "wednesday", label: "수요일" },
                  { key: "thursday", label: "목요일" },
                  { key: "friday", label: "금요일" },
                  { key: "saturday", label: "토요일" },
                  { key: "sunday", label: "일요일" },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-4">
                    <div className="w-20">
                      <Label>{label}</Label>
                    </div>
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        type="time"
                        value={businessHours[key].open}
                        onChange={(e) =>
                          handleBusinessHoursChange(key, "open", e.target.value)
                        }
                        disabled={businessHours[key].closed}
                        className="w-32"
                      />
                      <span className="text-muted-foreground">~</span>
                      <Input
                        type="time"
                        value={businessHours[key].close}
                        onChange={(e) =>
                          handleBusinessHoursChange(
                            key,
                            "close",
                            e.target.value
                          )
                        }
                        disabled={businessHours[key].closed}
                        className="w-32"
                      />
                      <div className="flex items-center gap-2 ml-4">
                        <input
                          type="checkbox"
                          checked={businessHours[key].closed}
                          onChange={(e) =>
                            handleBusinessHoursChange(
                              key,
                              "closed",
                              e.target.checked
                            )
                          }
                          className="rounded"
                        />
                        <Label className="text-sm text-muted-foreground">
                          휴무
                        </Label>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Claim 완료 업체만 메뉴/상품 등록 가능 */}
            {isEditMode && existingBusiness?.claimStatus === "claimed" && <Card>
                <CardHeader>
                  <CardTitle>
                    {formData.category === "restaurant" ? "메뉴 등록" : "상품/서비스 등록"}
                  </CardTitle>
                  <CardDescription>
                    출시 기간에는 모든 업체가 최대 10개까지 무료로 등록할 수 있습니다.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {products.length > 0 && (
                    <div className="space-y-2">
                      {products.map((product, index) => (
                        <div
                          key={product.id || index}
                          className="flex items-center justify-between p-3 border rounded-md"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {product.imageUrl && (
                              <img
                                src={product.imageUrl}
                                alt={product.nameKo || product.name}
                                className="w-16 h-16 object-cover rounded-md"
                              />
                            )}
                            <div className="flex-1">
                              <div className="font-medium">
                                {product.nameKo || product.name}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {product.price
                                  ? `$${product.price}`
                                  : "가격 협의"}
                              </div>
                              {product.description && (
                                <div className="text-xs text-muted-foreground mt-1 line-clamp-1">
                                  {product.description}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => editProduct(index)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeProduct(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showProductForm ? (
                    <div className="space-y-4 p-4 border rounded-md">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{formData.category === "restaurant" ? "메뉴명 (영문)" : "상품명 (영문)"} *</Label>
                          <Input
                            value={newProduct.name}
                            onChange={(e) =>
                              setNewProduct({
                                ...newProduct,
                                name: e.target.value,
                              })
                            }
                            placeholder={formData.category === "restaurant" ? "Menu Name" : "Product Name"}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{formData.category === "restaurant" ? "메뉴명 (한글)" : "상품명 (한글)"} *</Label>
                          <Input
                            value={newProduct.nameKo}
                            onChange={(e) =>
                              setNewProduct({
                                ...newProduct,
                                nameKo: e.target.value,
                              })
                            }
                            placeholder={formData.category === "restaurant" ? "메뉴명" : "상품명"}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>설명</Label>
                        <Textarea
                          value={newProduct.description}
                          onChange={(e) =>
                            setNewProduct({
                              ...newProduct,
                              description: e.target.value,
                            })
                          }
                          placeholder={formData.category === "restaurant" ? "메뉴 설명" : "상품 설명"}
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>가격 (AUD)</Label>
                          <Input
                            type="number"
                            value={newProduct.price}
                            onChange={(e) =>
                              setNewProduct({
                                ...newProduct,
                                price: e.target.value,
                              })
                            }
                            placeholder="0.00"
                            step="0.01"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>카테고리</Label>
                          <Input
                            value={newProduct.category || ""}
                            onChange={(e) =>
                              setNewProduct({
                                ...newProduct,
                                category: e.target.value,
                              })
                            }
                            placeholder="카테고리 (선택사항)"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{formData.category === "restaurant" ? "메뉴 이미지" : "상품 이미지"}</Label>
                        <MarketImageUploader
                          maxImages={1}
                          onImagesChange={(urls) => {
                            setProductImages(urls);
                            if (urls.length > 0) {
                              setNewProduct({
                                ...newProduct,
                                imageUrl: urls[0],
                              });
                            }
                          }}
                          userId={user?.uid}
                          initialImages={productImages}
                        />
                        <p className="text-xs text-muted-foreground">
                          이미지를 업로드하거나 URL을 직접 입력할 수 있습니다.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" onClick={addProduct}>
                          {editingProductIndex !== null ? "수정" : "추가"}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={cancelProductForm}
                        >
                          취소
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowProductForm(true)}
                      disabled={
                        products.length >=
                        10
                      }
                    >
                      <Package className="h-4 w-4 mr-2" />
                      {formData.category === "restaurant" ? "메뉴 추가" : "상품/서비스 추가"}
                    </Button>
                  )}
                </CardContent>
            </Card>}

            {/* Submit Buttons */}
            <div className="flex gap-4 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={loading}
              >
                이전 단계
              </Button>
              <Button type="submit" disabled={loading || loadingBusiness}>
                {loading ? (isEditMode ? "수정 중..." : "등록 중...") : (isEditMode ? "업체 수정" : "업체 등록")}
              </Button>
            </div>
          </form>
        )}

        {/* 상품 삭제 확인 다이얼로그 */}
        <AlertDialog
          open={deletingProductIndex !== null}
          onOpenChange={(open) => {
            if (!open) setDeletingProductIndex(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>상품 삭제</AlertDialogTitle>
              <AlertDialogDescription>
                정말로 이 상품을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeletingProductIndex(null)}>
                취소
              </AlertDialogCancel>
              <AlertDialogAction onClick={confirmRemoveProduct}>
                삭제
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
